import useQueryParams from 'hooks/useQueryParams'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { replace } from 'connected-react-router'
import { useAppDispatch } from 'hooks/reduxHooks'

const EXPERIENCES_SORT_BY_TYPE = 'sortBy'

export default function useExperiencesSortOptions(defaultSortBy: Experiences.SortTypes = 'type') {
  const intl = useIntl()
  const appDispatch = useAppDispatch()
  const queryParameters = useQueryParams()

  const SORT_OPTIONS: Utils.PartialRecord<Experiences.SortTypes, { label: string }> = useMemo(() => ({
    type: {
      label: intl.formatMessage({
        id: 'U3cPaB1Z85',
        defaultMessage: 'Recommended',
        description: 'Sort option label for recommended experiences in the experiences section',
      }),
    },
    latest: {
      label: intl.formatMessage({
        id: '3JVI+oSwpo',
        defaultMessage: 'Latest',
        description: 'Sort option label for latest experiences for the experience',
      }),
    },
    endingsoon: {
      label: intl.formatMessage({
        id: 'HnP1tdpY+M',
        defaultMessage: 'Ending Soon',
        description: 'Sort option label for ending soon experiences in the experiences section',
      }),
    },
    'price.asc': {
      label: intl.formatMessage({
        id: '6h21lst9LD',
        defaultMessage: 'Price (Lowest)',
        description: 'Sort option label for lowest price experiences in the experiences section',
      }),
    },
    'price.desc': {
      label: intl.formatMessage({
        id: 'w0gkJp3XAL',
        defaultMessage: 'Price (Highest)',
        description: 'Sort option label for highest price experiences in the experiences section',
      }),
    },
  }), [intl])
  const sortOptionFromUrl = queryParameters.get(EXPERIENCES_SORT_BY_TYPE)

  const sortBy = useMemo<Experiences.SortTypes>(() => {
    if (
      !sortOptionFromUrl ||
      !SORT_OPTIONS[sortOptionFromUrl as Experiences.SortTypes]
    ) return defaultSortBy
    return sortOptionFromUrl as Experiences.SortTypes
  }, [defaultSortBy, sortOptionFromUrl, SORT_OPTIONS])

  const setSortBy = useCallback((newSortBy: Experiences.SortTypes) => {
    const newUrlFilters = convertSortToUrl(newSortBy, queryParameters)
    appDispatch(replace({
      search: newUrlFilters.toString(),
    }))
  }, [appDispatch, queryParameters])

  const sortOptions = SORT_OPTIONS

  return {
    sortBy,
    setSortBy,
    sortOptions,
  }
}

function convertSortToUrl(newSortBy: Experiences.SortTypes, currentSearchParams: URLSearchParams) {
  const searchParams = new URLSearchParams(currentSearchParams)
  if (newSortBy) {
    searchParams.set(EXPERIENCES_SORT_BY_TYPE, String(newSortBy))
  }
  return searchParams
}
